import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, getToken } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { labelFor } from "@/lib/categories";
import { StarsDisplay } from "@/components/Stars";
import { useLang } from "@/context/LanguageContext";
import { formatMoney } from "@/lib/money";
import { useEstablishment } from "@/context/EstablishmentContext";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend, ComposedChart, Line,
} from "recharts";
import { Package, TrendingUp, Euro, CheckCircle2, BadgeCheck, Share2, Bot, MousePointerClick, Trash2, HeartHandshake, Gavel, Landmark, FileDown } from "lucide-react";

export default function Stats() {
  const { t, lang } = useLang();
  const { partner } = useAuth();
  const { scope, selectedId } = useEstablishment();
  const [d, setD] = useState(null);
  const [aiStats, setAiStats] = useState(null);
  const [boostClicks, setBoostClicks] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const year = new Date().getFullYear();
  // Basic stats are now available on every plan (incl. the Freemium trial).
  const isFree = false;

  useEffect(() => {
    if (isFree) return;
    api(scope("/analytics/dashboard")).then(setD).catch((e) => toast.error(e.message));
    api("/matching/stats?months=6").then(setAiStats).catch(() => {});
    api("/partners/me/boost-clicks").then(setBoostClicks).catch(() => {});
  }, [scope, selectedId, isFree]);

  if (isFree) {
    return (
      <div className="max-w-lg mx-auto text-center py-16" data-testid="stats-upgrade-gate">
        <div className="h-16 w-16 rounded-2xl findo-gradient grid place-items-center text-white mx-auto mb-5">
          <TrendingUp size={30} />
        </div>
        <h1 className="font-display text-2xl font-bold text-findo-navy">{t("stt.gateTitle")}</h1>
        <p className="text-slate-500 mt-2 text-sm leading-relaxed">
          {t("stt.gateDesc")}
        </p>
        <Link to="/b2b/account" data-testid="stats-upgrade-link">
          <button className="mt-6 rounded-xl findo-gradient text-white font-semibold px-6 py-3 hover:opacity-90 transition-opacity">
            {t("stt.gateCta")}
          </button>
        </Link>
      </div>
    );
  }

  async function shareMyRecap() {
    const imgUrl = `${API}/partners/me/year-recap-image.png?year=${year}`;
    const text = t("stt.recapText").replace("{year}", year);
    const url = window.location.origin;
    try {
      const res = await fetch(imgUrl, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error("img");
      const blob = await res.blob();
      const file = new File([blob], `findloop-bilan-${year}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: t("stt.recapTitle").replace("{year}", year), text, url });
        return;
      }
      // Fallback: download the image so the partner can attach it manually.
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl; a.download = `findloop-bilan-${year}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(dl);
      toast.success(t("stt.recapDownloaded"));
    } catch (e) { toast.error(t("stt.recapError")); }
  }

  async function exportDispositionsPdf() {
    setExportingPdf(true);
    try {
      const res = await fetch(`${API}/items/dispositions/export.pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("pdf");
      const blob = await res.blob();
      const disp = res.headers.get("Content-Disposition") || "";
      const m = disp.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : "findloop-dispositions.pdf";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(t("stt.dispoPdfError") === "stt.dispoPdfError" ? "Export PDF impossible." : t("stt.dispoPdfError"));
    } finally { setExportingPdf(false); }
  }

  if (!d) {
    return (
      <div className="p-6 md:p-10 max-w-7xl mx-auto">
        <PageHeader title={t("db.stats.t")} subtitle={t("db.stats.s")} />
        <div className="grid md:grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
      </div>
    );
  }

  const c = d.counts || {};
  const series = (d.daily_last_30d || []).map((x) => ({ ...x, date: x.date.slice(5) }));
  const topLost = (d.top_lost_categories || []).map((x) => ({ ...x, name: labelFor(x.category) }));
  const dispoTot = d.disposition_totals || { destroyed: 0, donated: 0, auctioned: 0 };
  const dispoByCat = (d.disposition_by_category || []).map((x) => ({ ...x, name: labelFor(x.category) }));

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader title={t("db.stats.t")} subtitle={t("db.stats.s")} testid="stats-header" />
        <button onClick={shareMyRecap} data-testid="share-my-recap-btn"
          className="inline-flex items-center gap-2 rounded-full bg-findo-indigo text-white text-sm font-semibold px-5 py-2.5 hover:bg-findo-indigo/90 transition-colors shrink-0">
          <Share2 size={15} /> {t("stt.shareRecap").replace("{year}", year)}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi Icon={Package} label={t("stt.kItems")} value={c.total_items || 0} tint="indigo" />
        <Kpi Icon={CheckCircle2} label={t("stt.kRestituted")} value={c.restituted || 0} tint="emerald" />
        <Kpi Icon={TrendingUp} label={t("stt.kRate")} value={`${d.restitution_rate_overall || 0}%`} tint="turquoise" />
        <Kpi Icon={Euro} label={t("stt.kRevenue")} value={formatMoney(d.revenue_month_eur || 0, lang, "EUR", { decimals: 0 })} tint="amber" />
      </div>

      {boostClicks && (
        <div className="mt-4 bg-white rounded-2xl border border-border p-5 card-shadow flex items-center gap-4" data-testid="boost-clicks-widget">
          <span className="h-12 w-12 grid place-items-center rounded-xl bg-findo-indigo/10 text-findo-indigo shrink-0"><MousePointerClick size={22} /></span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-500">{t("stt.boostClicks")}</p>
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-display font-bold text-slate-900" data-testid="boost-clicks-week">{boostClicks.this_week}</span>
              {boostClicks.last_week > 0 || boostClicks.this_week > 0 ? (
                <span className={`text-sm font-semibold ${boostClicks.trend === "up" ? "text-emerald-600" : boostClicks.trend === "down" ? "text-findo-red" : "text-slate-400"}`}>
                  {boostClicks.trend === "up" ? "▲" : boostClicks.trend === "down" ? "▼" : "▬"} {boostClicks.delta >= 0 ? "+" : ""}{boostClicks.delta} {t("stt.vsLastWeek")}
                </span>
              ) : null}
            </div>
          </div>
          {!boostClicks.boosted && (
            <span className="text-xs text-slate-400 shrink-0 max-w-[180px] text-right">{t("stt.boostInactive")}</span>
          )}
        </div>
      )}

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-border p-6 card-shadow">
          <h3 className="font-display font-bold text-slate-900 mb-4">{t("stt.activity30")}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={series} margin={{ left: -20 }}>
              <defs>
                <linearGradient id="gd" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3D3AB8" stopOpacity={0.35} /><stop offset="95%" stopColor="#3D3AB8" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gr" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00C2B8" stopOpacity={0.35} /><stop offset="95%" stopColor="#00C2B8" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={5} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} stroke="#94a3b8" />
              <Tooltip />
              <Area type="monotone" dataKey="declarations" name={t("stt.declarations")} stroke="#3D3AB8" fill="url(#gd)" strokeWidth={2} />
              <Area type="monotone" dataKey="restitutions" name={t("stt.restitutions")} stroke="#00C2B8" fill="url(#gr)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-border p-6 card-shadow">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="font-display font-bold text-slate-900">{t("stt.avgRating")}</h3>
              {d.ratings?.certified && <span className="inline-flex items-center gap-1 text-xs font-bold text-teal-700 bg-teal-100 px-2 py-0.5 rounded-full"><BadgeCheck size={13} /> {t("stt.certifiedPremium")}</span>}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-4xl font-display font-extrabold text-slate-900">{(d.ratings?.avg ?? 0).toFixed?.(1) ?? d.ratings?.avg ?? "0.0"}</span>
              <div>
                <StarsDisplay value={d.ratings?.avg || 0} />
                <p className="text-xs text-slate-400 mt-0.5">{d.ratings?.count || 0} {t("c.reviews")}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-border p-6 card-shadow">
            <h3 className="font-display font-bold text-slate-900 mb-2">{t("stt.inStorage")}</h3>
            <p className="text-3xl font-display font-extrabold text-findo-indigo">{c.in_storage || 0}</p>
            <p className="text-xs text-slate-400 mt-1">{t("stt.awaitingRestitution")}</p>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-border p-6 card-shadow">
        <h3 className="font-display font-bold text-slate-900 mb-4">{t("stt.topCategories")}</h3>
        {topLost.length === 0 ? (
          <p className="text-sm text-slate-400">{t("stt.noDeclYet")}</p>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={topLost} margin={{ left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} stroke="#94a3b8" />
              <Tooltip />
              <Bar dataKey="count" name={t("stt.declarations")} fill="#3D3AB8" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-border p-6 card-shadow" data-testid="disposition-stats">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="font-display font-bold text-slate-900 mb-1">{t("stt.unclaimedTitle")}</h3>
            <p className="text-sm text-slate-500">{t("stt.unclaimedDesc")}</p>
          </div>
          <button
            onClick={exportDispositionsPdf}
            disabled={exportingPdf}
            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-findo-navy text-white text-sm font-semibold px-4 py-2 hover:bg-findo-navy/90 disabled:opacity-60 transition-colors"
            data-testid="export-dispositions-pdf-btn">
            <FileDown size={16} />
            {exportingPdf ? (t("stt.dispoPdfExporting") === "stt.dispoPdfExporting" ? "Export…" : t("stt.dispoPdfExporting")) : (t("stt.dispoPdfBtn") === "stt.dispoPdfBtn" ? "Exporter PDF" : t("stt.dispoPdfBtn"))}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <DispoKpi Icon={Trash2} label={t("stt.destroyed")} value={dispoTot.destroyed || 0} tint="rose" testid="dispo-destroyed" />
          <DispoKpi Icon={HeartHandshake} label={t("stt.donatedAssoc")} value={dispoTot.donated || 0} tint="violet" testid="dispo-donated" />
          <DispoKpi Icon={Gavel} label={t("stt.auctionedSold")} value={dispoTot.auctioned || 0} tint="cyan" testid="dispo-auctioned" />
          <DispoKpi Icon={Landmark} label={t("stt.authorities")} value={dispoTot.handed_to_authorities || 0} tint="slate" testid="dispo-authorities" />
        </div>
        {dispoByCat.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="dispo-empty">{t("stt.dispoEmpty")}</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, dispoByCat.length * 42)}>
            <BarChart data={dispoByCat} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} stroke="#94a3b8" />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: "#475569" }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="destroyed" name={t("stt.destroyed")} stackId="d" fill="#f43f5e" />
              <Bar dataKey="donated" name={t("stt.donatedShort")} stackId="d" fill="#8b5cf6" />
              <Bar dataKey="auctioned" name={t("stt.auctionShort")} stackId="d" fill="#06b6d4" />
              <Bar dataKey="handed_to_authorities" name={t("stt.authShort")} stackId="d" fill="#64748b" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-border p-6 card-shadow" data-testid="ai-match-stats">
        <div className="flex items-center gap-2 mb-1">
          <Bot size={18} className="text-findo-indigo" />
          <h3 className="font-display font-bold text-slate-900">{t("stt.aiTitle")}</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">{t("stt.ai6mo")}</p>
        {!aiStats || aiStats.every((m) => m.total === 0) ? (
          <p className="text-sm text-slate-400" data-testid="ai-match-stats-empty">{t("stt.aiEmpty")}</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={aiStats.map((m) => ({ ...m, label: m.month ? new Date(`${m.month}-01T00:00:00`).toLocaleDateString(lang, { month: "short", year: "2-digit" }) : m.label }))} margin={{ left: -20, right: 6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} allowDecimals={false} stroke="#94a3b8" />
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} stroke="#6366f1" width={44} />
              <Tooltip formatter={(v, n) => (n === t("stt.acceptRate") ? [`${v}%`, n] : [v, n])} />
              <Legend />
              <Bar yAxisId="left" dataKey="accepted" name={t("stt.accepted")} fill="#10b981" radius={[6, 6, 0, 0]} />
              <Bar yAxisId="left" dataKey="rejected" name={t("stt.rejected")} fill="#f43f5e" radius={[6, 6, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="acceptance_rate" name={t("stt.acceptRate")}
                stroke="#6366f1" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

const TINTS = {
  indigo: "bg-findo-indigo/10 text-findo-indigo",
  emerald: "bg-emerald-100 text-emerald-700",
  turquoise: "bg-teal-100 text-teal-700",
  amber: "bg-amber-100 text-amber-700",
  rose: "bg-rose-100 text-rose-700",
  violet: "bg-violet-100 text-violet-700",
  cyan: "bg-cyan-100 text-cyan-700",
  slate: "bg-slate-200 text-slate-700",
};

function DispoKpi({ Icon, label, value, tint, testid }) {
  return (
    <div className="rounded-xl border border-border p-3.5 text-center" data-testid={testid}>
      <span className={`h-9 w-9 mx-auto grid place-items-center rounded-lg ${TINTS[tint]}`}><Icon size={17} /></span>
      <p className="mt-2 text-2xl font-display font-extrabold text-slate-900" data-testid={`${testid}-value`}>{value}</p>
      <p className="text-[11px] text-slate-500 font-medium">{label}</p>
    </div>
  );
}

function Kpi({ Icon, label, value, tint }) {
  return (
    <div className="bg-white rounded-2xl border border-border p-5 card-shadow" data-testid={`kpi-${label}`}>
      <span className={`h-10 w-10 grid place-items-center rounded-xl ${TINTS[tint]}`}><Icon size={20} /></span>
      <p className="mt-3 text-2xl font-display font-extrabold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500 font-medium">{label}</p>
    </div>
  );
}
